import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { fmtDate } from '../lib/format';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Plus, Trash2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const ROLES = ['owner', 'manager', 'cashier', 'waiter', 'kitchen', 'delivery', 'accountant', 'auditor'];

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'cashier', pin: '1234' });

  const load = async () => { const [s, l] = await Promise.all([api.get('/staff'), api.get('/audit-logs')]); setStaff(s.data); setLogs(l.data); };
  useEffect(() => { load(); }, []);

  const add = async () => { try { await api.post('/staff', form); setOpen(false); setForm({ name: '', email: '', password: '', role: 'cashier', pin: '1234' }); load(); toast.success('Staff added'); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const del = async (s) => { await api.delete(`/staff/${s.id}`); load(); toast.success('Removed'); };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <h1 className="font-display text-2xl font-bold">Staff & Roles</h1>
      <PageHelp id="staff">Add team members with roles — permissions are enforced server-side on every action. Use the top-bar <b>PIN</b> button to switch users fast at the terminal. The <b>Audit Log</b> records every sensitive action (checkouts, discounts, stock changes).</PageHelp>
      <Tabs defaultValue="staff">
        <TabsList><TabsTrigger value="staff">Team</TabsTrigger><TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger></TabsList>
        <TabsContent value="staff" className="space-y-3 pt-3">
          <Button data-testid="add-staff-button" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" /> Add Staff</Button>
          <Card className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>PIN</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>{staff.map((s) => (
              <TableRow key={s.id} data-testid={`staff-row-${s.email}`}><TableCell className="font-medium">{s.name}</TableCell><TableCell className="text-sm text-muted-foreground">{s.email}</TableCell><TableCell><Badge variant="secondary" className="capitalize">{s.role}</Badge></TableCell><TableCell className="font-mono">{s.pin}</TableCell><TableCell>{s.role !== 'owner' && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(s)}><Trash2 className="h-3.5 w-3.5" /></Button>}</TableCell></TableRow>
            ))}</TableBody>
          </Table></Card>
        </TabsContent>
        <TabsContent value="audit" className="pt-3">
          <Card className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>User</TableHead><TableHead>Role</TableHead><TableHead>Time</TableHead></TableRow></TableHeader>
            <TableBody>{logs.map((l) => (
              <TableRow key={l.id}><TableCell><Badge variant="outline" className="capitalize">{l.action.replace(/_/g, ' ')}</Badge></TableCell><TableCell>{l.entity}</TableCell><TableCell>{l.user_name}</TableCell><TableCell className="capitalize text-muted-foreground">{l.role}</TableCell><TableCell className="text-xs text-muted-foreground">{fmtDate(l.at)}</TableCell></TableRow>
            ))}</TableBody>
          </Table>{logs.length === 0 && <div className="p-6 text-center text-muted-foreground">No audit entries yet</div>}</Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Add Staff</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input data-testid="staff-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Password</Label><Input data-testid="staff-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div><Label>PIN</Label><Input data-testid="staff-pin" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} /></div>
            </div>
            <div><Label>Role</Label><select data-testid="staff-role" className="w-full h-9 rounded-md border bg-background px-2 capitalize" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select></div>
            <Button data-testid="staff-save" className="w-full" onClick={add}>Add</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
